/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { OnboardingStore } from '@app/+onboarding/onboarding.store';
import { OnboardingUtils } from '@app/+onboarding/onboarding.utils';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.service';
import { UserGuideModel, UserGuideStatus, UserGuideType } from '@app/reducers/orm/user-guide/user-guide.model';
import {
  getUserGuideEntities,
  isUserGuideFinished,
  selectUserGuideByType,
} from '@app/reducers/orm/user-guide/user-guide.selector';
import { UserGuideService } from '@app/reducers/orm/user-guide/user-guide.service';
import { getPageParamsState } from '@app/reducers/page-params-state/page-params.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ChecklistItemModel, ChecklistModel, SbDialogService } from '@sb/ui';
import { roundToDecimal } from '@shiftbase-com/utilities';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import { AppState } from '../../reducers';
import { getSelfOnboarding } from '../../reducers/account/account.service';
import {
  InternalOnboardingStep,
  ManagerOnboardingChecklistStep,
  OnboardingChecklistStep,
  SmbHospitalityOnboardingChecklistStep,
  SmbHospitalityOnboardingChecklistStepKeys,
} from './onboarding-checklist.model';

interface UserGuideModelEnchanced extends UserGuideModel {
  completedSteps: OnboardingChecklistStep[];
}

type SupportedUserGuideTypes = UserGuideType.MANAGER_ONBOARDING_CHECKLIST | UserGuideType.SELF_ONBOARDING_CHECKLIST;

@Injectable({
  providedIn: 'root',
})
export class OnboardingChecklistService {
  public totalSteps: number;

  public authUser$ = this.store.select(getAuthenticatedUser);
  public scheduleLink: string[] | any[];

  public showChecklist: boolean;

  public checklistDismissed$ = new Subject<UserGuideType>();

  public userGuideType: UserGuideType;

  private OnboardingStore = inject(OnboardingStore);

  private userGuide$: Observable<UserGuideModelEnchanced> = this.store.select(getUserGuideEntities).pipe(
    map((userGuideEntities) => userGuideEntities[this.userGuideType]),
    map((guide) => {
      const shouldKeepChecklistActive =
        guide?.status === UserGuideStatus.IN_PROGRESS ||
        (this.userGuideType === UserGuideType.SELF_ONBOARDING_CHECKLIST && guide?.status !== UserGuideStatus.DISMISSED);

      if (guide && shouldKeepChecklistActive) {
        return {
          ...guide,
          completedSteps: this.transformUserGuideStepToChecklistSteps(guide.step),
        };
      }
      return null;
    }),
  );

  public isChecklistInProgress$: Observable<boolean> = this.userGuide$.pipe(
    withLatestFrom(this.store.select(getPermissionState)),
    map(([guide, permissionState]) => {
      if (!permissionState.isAccountManager) {
        return false;
      }

      if (!guide) {
        return false;
      }

      return guide.status === UserGuideStatus.IN_PROGRESS;
    }),
  );

  public completedSteps$ = new BehaviorSubject<OnboardingChecklistStep[]>(undefined);

  public get completedSteps(): OnboardingChecklistStep[] | undefined {
    return this.completedSteps$.value;
  }

  /**
   * TODO get rid of this thing. This isn't pure at all.
   * - Progress and remainingSteps can just be calculated in checklist component itself
   * - Labels maybe even as well as there probably isn't any other labels envisioned as the ones composed in authenticated component
   * - scheduleLink is only depending on pageParams and can be determined completely separately from fetching the guide
   * - The name of this observable doesn't make sense either, it doesn't return items, it returns a checklist model
   */

  public items$: Observable<ChecklistModel> = this.userGuide$.pipe(
    withLatestFrom(
      this.store.select(getPermissionState),
      this.store.select(getPageParamsState),
      this.store.pipe(select(isUserGuideFinished(UserGuideType.SIGNUP_GREETING_MODAL))),
    ),
    map(([guide, permissionState, pageParams, signupGreetingFinished]) => {
      if (pageParams) {
        this.scheduleLink = [
          'schedule',
          pageParams.schedule.mode,
          pageParams.schedule.period,
          { date: pageParams.schedule.date },
        ];
      }

      if (guide?.guide === UserGuideType.SELF_ONBOARDING_CHECKLIST && guide?.completedSteps) {
        // Keep only visible steps
        guide.completedSteps = guide.completedSteps?.filter((step: SmbHospitalityOnboardingChecklistStep) =>
          this.onboardingUtils.canAccessStep(step),
        );
      }

      this.completedSteps$.next(guide?.completedSteps);
      const items = this.items;
      this.totalSteps = items.length;

      if (!guide) {
        return null;
      }

      // TODO manager onboarding specific logic. Should be moved out of it.
      if (this.userGuideType === UserGuideType.MANAGER_ONBOARDING_CHECKLIST) {
        if (permissionState.isAccountManager === false) return null;
        if (signupGreetingFinished) {
          this.showChecklist = true;
        }
      } else {
        this.showChecklist = guide.status === UserGuideStatus.IN_PROGRESS;
      }

      const progress = roundToDecimal((this.completedSteps.length / this.totalSteps) * 100, 0);
      const remainingSteps = this.totalSteps - this.completedSteps.length;
      return {
        progress,
        items,
        remainingSteps,
      };
    }),
  );

  public constructor(
    private readonly store: Store<AppState>,
    private readonly userGuideService: UserGuideService,
    private readonly translate: TranslateService,
    private readonly dialog: SbDialogService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
    private readonly onboardingUtils: OnboardingUtils,
  ) {
    void this.store
      .select(getSelfOnboarding)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selfOnboarding) => {
        if (selfOnboarding) {
          this.userGuideType = UserGuideType.SELF_ONBOARDING_CHECKLIST;
        } else {
          this.userGuideType = UserGuideType.MANAGER_ONBOARDING_CHECKLIST;
        }
      });
  }

  public initGuide(guide: SupportedUserGuideTypes): void {
    void this.userGuideService
      .trackUserGuide({
        guide,
        status: UserGuideStatus.IN_PROGRESS,
        // TODO manager onboarding specific logic. Should be moved out of it. Signup component should mark this step as completed.
        step:
          guide === UserGuideType.MANAGER_ONBOARDING_CHECKLIST ? ManagerOnboardingChecklistStep.COMPLETE_SIGNUP : '',
      })
      .pipe(take(1))
      .subscribe();
  }

  public hasSelfOnboardingChecklist(): Observable<boolean> {
    return this.store
      .select(selectUserGuideByType(UserGuideType.SELF_ONBOARDING_CHECKLIST))
      .pipe(map((userGuide) => !!userGuide));
  }

  public resumeGuide(): void {
    void this.store
      .select(selectUserGuideByType(this.userGuideType))
      .pipe(
        take(1),
        switchMap((userGuide) =>
          this.userGuideService.trackUserGuide({
            guide: this.userGuideType,
            status: UserGuideStatus.IN_PROGRESS,
            step: userGuide.step,
          }),
        ),
      )
      .subscribe();
  }

  // Emits false if it the step was already completed (early return)
  public completeStep(guide: UserGuideType, step: OnboardingChecklistStep): Observable<boolean> {
    // Right now it is only possible to have one onboarding checklist guide active at a time.
    // Only mark the step as completed if it is part of the active guide.
    if (!this.completedSteps || this.completedSteps.includes(step) || guide !== this.userGuideType) {
      return of(false);
    }

    const completedSteps = [...this.completedSteps, step];
    const guideStatus =
      completedSteps.length === this.totalSteps ? UserGuideStatus.FINISHED : UserGuideStatus.IN_PROGRESS;

    return this.userGuideService
      .trackUserGuide({
        guide: this.userGuideType,
        status: guideStatus,
        step: this.transformChecklistStepsToUserGuideStep(completedSteps),
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }

  public dismiss(): Observable<void> {
    if (this.userGuideType === UserGuideType.SELF_ONBOARDING_CHECKLIST) {
      return this.store.select(selectUserGuideByType(this.userGuideType)).pipe(
        take(1),
        switchMap((userGuide) =>
          this.userGuideService.trackUserGuide({
            guide: this.userGuideType,
            status: UserGuideStatus.DISMISSED,
            step: userGuide.step,
          }),
        ),
        tap(() => this.checklistDismissed$.next(this.userGuideType)),
      );
    }

    return this.dialog
      .openConfirm({
        type: 'warning',
        title: this.translate.instant('Dismiss setup guide?'),
        description: this.translate.instant(
          'If you dismiss it now, you might miss out on valueable tips and insights that can streamline your journey.',
        ),
        primary: {
          text: this.translate.instant('Dismiss'),
        },
      })
      .closed.pipe(
        filter((result) => result?.confirmed),
        switchMap(() =>
          this.userGuideService.trackUserGuide({
            guide: this.userGuideType,
            status: UserGuideStatus.DISMISSED,
            step: null,
          }),
        ),
      );
  }

  private isCompletedStep(step: OnboardingChecklistStep): boolean {
    return this.completedSteps?.includes(step) ?? false;
  }

  private get items(): ChecklistItemModel[] {
    switch (this.userGuideType) {
      case UserGuideType.MANAGER_ONBOARDING_CHECKLIST:
        return this.managerOnboardingChecklistItems;
      case UserGuideType.SELF_ONBOARDING_CHECKLIST:
        return this.selfOnboardingChecklistItems;
      default:
        return [];
    }
  }

  private get managerOnboardingChecklistItems(): ChecklistItemModel[] {
    return [
      {
        title: this.translate.instant('Complete your signup'),
        isCompleted: this.isCompletedStep(ManagerOnboardingChecklistStep.COMPLETE_SIGNUP),
      },
      {
        title: this.translate.instant('Setup your company’s organization'),
        isCompleted: this.isCompletedStep(ManagerOnboardingChecklistStep.SETUP_ORGANIZATION_SETUP),
        action: () => this.router.navigate(['account/settings/locations']),
      },
      {
        title: this.translate.instant('Add your employees'),
        isCompleted: this.isCompletedStep(ManagerOnboardingChecklistStep.ADD_EMPLOYEES),
        action: () => this.router.navigate(['employees']),
      },
      {
        title: this.translate.instant('Schedule an employee'),
        isCompleted: this.isCompletedStep(ManagerOnboardingChecklistStep.SCHEDULE_EMPLOYEE),
        action: () => this.router.navigate(this.scheduleLink),
      },
      {
        title: this.translate.instant('Verify your email address'),
        isCompleted: this.isCompletedStep(ManagerOnboardingChecklistStep.VERIFY_EMAIL),
        action: () => this.router.navigate(['my-account/login']),
      },
    ];
  }

  private get selfOnboardingChecklistItems(): ChecklistItemModel[] {
    const config = this.getSelfOnboardingChecklistSteps();

    // We don't iterate over the enum directly, to guarantee the order of the steps.
    const selfOnboardingStepList: SmbHospitalityOnboardingChecklistStepKeys[] = [
      SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT,
      SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE,
      SmbHospitalityOnboardingChecklistStep.TIME_TRACKING,
      SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT,
      SmbHospitalityOnboardingChecklistStep.PAYROLL,
      SmbHospitalityOnboardingChecklistStep.MOBILE_APP,
      SmbHospitalityOnboardingChecklistStep.INVITE_TEAM,
    ];

    return selfOnboardingStepList
      .filter((step: SmbHospitalityOnboardingChecklistStep) => this.onboardingUtils.canAccessStep(step))
      .map((step) => config[step]);
  }

  private getSelfOnboardingChecklistSteps(): Record<SmbHospitalityOnboardingChecklistStep, ChecklistItemModel> {
    return {
      [SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT]: {
        title: this.translate.instant('Add your first shift'),
        subtitle: this.translate.instant('Simplify scheduling by adding your first shift now'),
        expanded: true,
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE]: {
        title: this.translate.instant('Optimise your schedule'),
        subtitle: this.translate.instant('Easily manage availability and handle last-minute changes'),
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE),
          },
          secondary: {
            text: this.translate.instant('Show me'),
            action: () =>
              this.OnboardingStore.updateState(InternalOnboardingStep.OPTIMIZED_SCHEDULE_DEMO, { skipFlow: true }),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.TIME_TRACKING]: {
        title: this.translate.instant('Track employee hours'),
        subtitle: this.translate.instant('Track hours with accuracy and avoid costly payroll mistakes'),
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.TIME_TRACKING),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.TIME_TRACKING),
          },
          secondary: {
            text: this.translate.instant('Show me'),
            action: () =>
              this.OnboardingStore.updateState(InternalOnboardingStep.TIME_TRACKING_DEMO, { skipFlow: true }),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT]: {
        title: this.translate.instant('Manage employee absences'),
        subtitle: this.translate.instant('Stay on top of absences and prevent scheduling chaos'),
        isCompleted: this.completedSteps
          ? this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT)
          : false,
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT),
          },
          secondary: {
            text: this.translate.instant('Show me'),
            action: () =>
              this.OnboardingStore.updateState(InternalOnboardingStep.ABSENCE_MANAGEMENT_DEMO, { skipFlow: true }),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.PAYROLL]: {
        title: this.translate.instant('Connect your payroll'),
        subtitle: this.translate.instant('Sync payroll today to eliminate delays and manual work'),
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.PAYROLL),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.PAYROLL),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.MOBILE_APP]: {
        title: this.translate.instant('Download mobile app'),
        subtitle: this.translate.instant('Manage your business on the go'),
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.MOBILE_APP),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.OnboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.MOBILE_APP),
          },
        },
      },
      [SmbHospitalityOnboardingChecklistStep.INVITE_TEAM]: {
        title: this.translate.instant('Invite your team'),
        subtitle: this.translate.instant('Send invites to your employees and get them started!'),
        isCompleted: this.isCompletedStep(SmbHospitalityOnboardingChecklistStep.INVITE_TEAM),
        actions: {
          primary: {
            text: this.translate.instant('Start'),
            action: () => this.router.navigate(['', { outlets: { modal: ['onboarding-invite-employees'] } }]),
          },
        },
      },
    };
  }

  public getChecklistTitles(level: number): string {
    const checklistTitles = new Map<number, string>([
      [0, _("Let's begin, [name]! 🚀")],
      [1, _("Nice work, [name]! You're off to a strong start. 💪")],
      [25, _('Great progress, [name]! Keep moving forward. 🌟')],
      [50, _("You're halfway there, [name]! Keep it up! 🔥")],
      [70, _("Well done, [name]! You're almost there. ⏳")],
      [85, _("You're nearly finished, [name]. Just a little more! 🎯")],
      [100, _('Congratulations, [name]! Onboarding is complete! 🎉')],
    ]);

    // If level exactly matches a key, return the corresponding title
    if (checklistTitles.has(level)) {
      return checklistTitles.get(level);
    }

    // Find the nearest lower-bound key (largest key ≤ level)
    let nearestTitle = checklistTitles.get(0);
    for (const key of [...checklistTitles.keys()].sort((a, b) => a - b)) {
      if (level >= key) {
        nearestTitle = checklistTitles.get(key);
      } else {
        break;
      }
    }

    return nearestTitle;
  }

  /**
   * Userguides are set up in the backend with an optional singular step (i.e. the current/last completed step).
   * Rather than letting the backend properly support a user guide with multiple active steps, the onboarding checklist abuses the step field to store a comma separated list of steps.
   * We therefore need to transform the api response back and forth.
   */
  private transformUserGuideStepToChecklistSteps(input: string): OnboardingChecklistStep[] {
    if (input === '' || input === null) {
      return [];
    }
    return input.split(',').map((step) => step.trim() as OnboardingChecklistStep);
  }

  private transformChecklistStepsToUserGuideStep(input: OnboardingChecklistStep[]): string {
    return input.map((step) => step.trim()).join(',');
  }
}
